/**
 * `/sign-in` — the email one-time-code flow.
 *
 * Two steps, and BOTH live in the URL: a wizard step is
 * a query param. `/sign-in?step=code&requestId=…` reopens exactly where a fan
 * was after they switched to their mail app to read the code and came back —
 * which on a phone is the normal path through this screen, not the edge case.
 *
 * Every call here is a generated hook. An empty field is
 * inline validation under the field, never the error modal;
 * a refused code is the modal.
 */
import {
  useEmailCheckDeliverable,
  useOauthProviderConfigGet,
  useOtpRequest,
  useOtpVerify,
} from "@sportsmet/auth-sdk/public";
import { Button, Card, InlineBanner, Input } from "@sportsmet/ui";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  type ReactNode,
  type SubmitEventHandler,
  useEffect,
  useState,
} from "react";

import { useAuth } from "../auth/AuthProvider";
import { getAuthState } from "../auth/getAuthState";
import { safeRedirectPath } from "../auth/safeRedirectPath";
import { showApiError } from "../errors/apiErrorBus";
import { DELETE_ACCOUNT_DONE_MESSAGE } from "../features/account/deleteAccountCopy";
import {
  CREATE_POOL_SIGN_IN_CONTEXT,
  isCreatePoolIntent,
} from "../features/signIn/createPoolIntent";
import { ProviderSignInPanel } from "../features/signIn/ProviderSignInPanel";
import { getRecaptchaToken } from "../features/signIn/recaptcha";
import {
  resolveResendControl,
  secondsUntil,
} from "../features/signIn/resendCountdown";
import { SignInFinePrint } from "../features/signIn/SignInFinePrint";
import {
  coerceBoolean,
  coerceEnum,
  coerceOptionalString,
} from "../routeState/searchParams";

export const SIGN_IN_STEPS = ["email", "code"] as const;
export type typeSignInStep = (typeof SIGN_IN_STEPS)[number];

export type typeSignInSearch = {
  step: typeSignInStep;
  requestId?: string | undefined;
  /** Minutes the emailed code stays valid. From /otp/request. */
  expiresInMinutes?: number | undefined;
  /**
   * ISO instant the next code may be requested at. From /otp/request, so the
   * countdown tracks the server's live interval rather than a baked-in 30
   * seconds. Absent on a restored or bookmarked code-step URL.
   */
  nextRequestAllowedAt?: string | undefined;
  /** The address a code was requested for. Absent on a restored URL. */
  email?: string | undefined;
  redirect?: string | undefined;
  deleted?: boolean | undefined;
};

export { safeRedirectPath };

/**
 * The post-sign-in target is a whole href (`/pools?poolId=abc&sheet=picks`),
 * not one of the router's literal route paths, so it travels as `href` rather
 * than `to`. `reloadDocument: false` is explicit: the router infers a
 * full-document navigation for an ABSOLUTE href, and a fan who just signed in
 * should not watch the app boot a second time.
 */
const REDIRECT_NAVIGATION = { reloadDocument: false } as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Neither the deliverability pre-check nor the reCAPTCHA token mint may ever
// hang the submit button: both are couriers, not gates. `otp/request`'s own
// unconditional re-check on the server is what actually protects the
// address, so a slow or unreachable dependency here falls back to letting the
// fan submit, never to refusing on their behalf.
const DELIVERABILITY_CHECK_TIMEOUT_MS = 2500;
const RECAPTCHA_TOKEN_TIMEOUT_MS = 3000;

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  fallback: T,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(() => {
        resolve(fallback);
      }, ms);
    }),
  ]);
}

// The one sentence both the client-side check (below) and the server's own
// EMAIL_ADDRESS_REFUSED/mxRecordMissing re-check render with.
const UNDELIVERABLE_EMAIL_MESSAGE =
  "That address can't receive email. Check the spelling.";

const SignInScreen = (): ReactNode => {
  // `useSearch({ from })` rather than `Route.useSearch()`: this component is
  // referenced by the `Route` declaration at the bottom of the file, so reading
  // off `Route` here is a circular type reference that TypeScript resolves to
  // `any` — silently turning every search param into an unchecked value.
  const search = useSearch({ from: "/sign-in" });
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>(undefined);
  // ISSUE-108: `requestOtp.isPending` alone used to be the whole submit
  // pipeline, so it was enough to drive the button's loading/disabled state.
  // Now the deliverability check and the reCAPTCHA token mint run BEFORE
  // requestOtp.mutate ever fires, and neither is reflected by that flag, so
  // without this the button stays enabled with no spinner for up to ~6
  // seconds (DELIVERABILITY_CHECK_TIMEOUT_MS + RECAPTCHA_TOKEN_TIMEOUT_MS
  // worst case) and a double tap can fire two concurrent submissions.
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  const requestOtp = useOtpRequest();
  const verifyOtp = useOtpVerify();
  const checkDeliverable = useEmailCheckDeliverable();
  // Also read by ProviderSignInPanel below, and react-query dedupes both
  // calls against the same query key, so this is not a second network request.
  const providerConfig = useOauthProviderConfigGet({});

  // A `step=code` URL with no request id cannot be completed — a fan who
  // bookmarked it, or whose code expired days ago, starts over rather than
  // typing into a form that was always going to fail.
  const step: typeSignInStep =
    search.step === "code" && search.requestId !== undefined ? "code" : "email";

  // The countdown re-reads the clock once a second while a gap is running. The
  // dependency is `nextRequestAllowedAt` itself, so a resend's fresh instant
  // restarts the interval rather than continuing the old one.
  const [tick, setTick] = useState(() => ({
    instant: search.nextRequestAllowedAt,
    nowMs: Date.now(),
  }));
  // No gap running means no interval ticking, so `tick` can be minutes stale by
  // the time a resend hands over a new instant. Reading the clock directly for
  // an instant the tick has not caught up to keeps the FIRST paint honest,
  // instead of flashing a countdown measured from the wrong moment.
  const nowMs =
    tick.instant === search.nextRequestAllowedAt ? tick.nowMs : Date.now();
  useEffect(() => {
    const instant = search.nextRequestAllowedAt;
    setTick({ instant, nowMs: Date.now() });
    if (instant === undefined) return;
    if (secondsUntil(instant, Date.now()) <= 0) return;
    const interval = setInterval(() => {
      const tickMs = Date.now();
      setTick({ instant, nowMs: tickMs });
      // Nothing left to count. Stop, rather than re-rendering the screen once
      // a second for as long as the fan sits on it.
      if (secondsUntil(instant, tickMs) <= 0) clearInterval(interval);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [search.nextRequestAllowedAt]);

  const resend = resolveResendControl({
    nextRequestAllowedAt: search.nextRequestAllowedAt,
    isRequestPending: requestOtp.isPending,
    nowMs,
  });

  /**
   * Sends the code and moves the wizard to the code step.
   *
   * Shared by the email form and the resend control on purpose: a resend that
   * wrote a different set of search params than the first request would leave
   * the second countdown reading a stale `nextRequestAllowedAt`.
   */
  function requestCodeFor(
    address: string,
    doReplaceStep = false,
    recaptchaToken?: string,
  ): void {
    // True for the email step's own submit, false for the resend control on
    // the code step (`onResend` below always passes `doReplaceStep: true`).
    const isInitialSubmit = !doReplaceStep;
    requestOtp.mutate(
      {
        email: address,
        client: "web",
        ...(recaptchaToken ? { recaptchaToken } : {}),
      },
      {
        onSuccess: (result) => {
          // ISSUE-108 R3: otp/request's own unconditional re-check is the
          // real control, and the client-side pre-check in runSubmitEmail
          // below is only a courtesy. Diverted to the same inline field error only
          // for the initial email-step submission, where that field is on
          // screen; a resend fires from the code step, where `emailError`
          // renders nowhere, so a resend's own refusal still falls through to
          // the shared error modal exactly as it did before this issue.
          const refusalDetails = result.error?.details as
            { reason?: string } | undefined;
          if (
            isInitialSubmit &&
            result.error?.code === "EMAIL_ADDRESS_REFUSED" &&
            refusalDetails?.reason === "mxRecordMissing"
          ) {
            setEmailError(UNDELIVERABLE_EMAIL_MESSAGE);
            return;
          }
          if (showApiError(result)) return;
          const requestId = result.data?.requestId;
          // Empty 200 envelope: no error code and no requestId. Fail visible,
          // not silent. Resend uses this same helper and has no email field.
          if (requestId === undefined) {
            showApiError({ error: { code: "INTERNAL_ERROR" } });
            return;
          }
          const expiresInSeconds = result.data?.expiresInSeconds;
          const expiresInMinutes =
            expiresInSeconds === undefined
              ? 10
              : Math.max(1, Math.round(expiresInSeconds / 60));
          void navigate({
            to: "/sign-in",
            // A resend REPLACES the code step it was tapped on. Pushing would
            // leave a superseded `requestId` one Back away, on a screen that
            // still looks live and verifies against a dead request.
            replace: doReplaceStep,
            search: {
              step: "code",
              requestId,
              expiresInMinutes,
              nextRequestAllowedAt: result.data?.nextRequestAllowedAt,
              email: address,
              ...(search.redirect === undefined
                ? {}
                : { redirect: search.redirect }),
            },
          });
        },
      },
    );
  }

  /**
   * ISSUE-108: before `otp/request` fires, checks deliverability (blocking
   * inline on a refusal, R2) and mints a reCAPTCHA v3 token (R1). Both are
   * timeout-bounded couriers, never gates. See `DELIVERABILITY_CHECK_TIMEOUT_MS`
   * and `RECAPTCHA_TOKEN_TIMEOUT_MS` above: a slow or unreachable dependency
   * always still lets the fan submit (S7).
   */
  async function runSubmitEmail(): Promise<void> {
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError("Enter the email address you use for Sportsmet.");
      return;
    }
    setEmailError(undefined);

    // Covers the button's loading/disabled state for the two async steps
    // below, neither of which `requestOtp.isPending` reflects on its own
    // (see `isSubmittingEmail`'s declaration comment above).
    setIsSubmittingEmail(true);
    try {
      const deliverability = await withTimeout(
        checkDeliverable.mutateAsync({ email: trimmed }).catch(() => undefined),
        DELIVERABILITY_CHECK_TIMEOUT_MS,
        undefined,
      );
      if (deliverability?.data?.isDeliverable === false) {
        setEmailError(UNDELIVERABLE_EMAIL_MESSAGE);
        return;
      }

      const recaptchaToken = await withTimeout(
        // The type says `recaptcha` is required, but that type is an unchecked
        // cast rather than a checked fact: the generated SDK compiles an ajv
        // validator for the REQUEST body only, and hands back the response as
        // `as typeOauthProviderConfigGetOutput200`. Nothing verifies the wire
        // shape. auth-api and web deploy as separate services with no ordering
        // guarantee, so a new web bundle can reach an auth-api that predates
        // ISSUE-108 and omits the field, and an unguarded read throws a
        // TypeError at a fan in the middle of signing in. A type is a claim
        // about a remote server, and a stale server has not read it.
        // oxlint-disable-next-line typescript/no-unnecessary-condition -- see the comment above: the response type is an unchecked cast and a pre-ISSUE-108 auth-api omits this field
        getRecaptchaToken(providerConfig.data?.data?.recaptcha?.siteKey),
        RECAPTCHA_TOKEN_TIMEOUT_MS,
        undefined,
      );

      requestCodeFor(trimmed, false, recaptchaToken);
    } finally {
      setIsSubmittingEmail(false);
    }
  }

  const onSubmitEmail: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void runSubmitEmail();
  };

  /**
   * A new code for the SAME address, only once the server's own
   * `nextRequestAllowedAt` has passed.
   *
   * A refused resend falls through to `showApiError` inside `requestCodeFor` —
   * `RATE_LIMITED` reaches the fan as the shared class sentence, and the
   * countdown on screen is left exactly as it was, because the code already in
   * their inbox is still valid.
   */
  function onResend(): void {
    if (resend.isDisabled) return;
    if (search.email === undefined) {
      // An old link, from before the address traveled in the URL. There is
      // nothing to resend TO, so the fan is taken one step back to type it
      // rather than shown a control that quietly does nothing.
      void navigate({
        to: "/sign-in",
        search: {
          step: "email",
          ...(search.redirect === undefined
            ? {}
            : { redirect: search.redirect }),
        },
      });
      return;
    }
    requestCodeFor(search.email, true);
  }

  const onSubmitCode: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (trimmed === "") {
      setCodeError("Enter the code we emailed you.");
      return;
    }
    setCodeError(undefined);
    const requestId = search.requestId;
    if (requestId === undefined) return;

    verifyOtp.mutate(
      { requestId, code: trimmed },
      {
        onSuccess: (result) => {
          if (showApiError(result, "accountState")) return;
          const session = result.data;
          if (!session) return;
          signIn({
            accessToken: session.accessToken,
            refreshToken: session.refreshToken,
          });
          void navigate({
            href: safeRedirectPath(search.redirect),
            ...REDIRECT_NAVIGATION,
          });
        },
      },
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4 py-6">
      {search.deleted ? (
        <InlineBanner
          variant="neutral"
          message={DELETE_ACCOUNT_DONE_MESSAGE}
          onDismiss={() => {
            void navigate({
              to: "/sign-in",
              search: {
                step: search.step,
                requestId: search.requestId,
                expiresInMinutes: search.expiresInMinutes,
                nextRequestAllowedAt: search.nextRequestAllowedAt,
                email: search.email,
                redirect: search.redirect,
              },
              replace: true,
            });
          }}
        />
      ) : null}
      <div className="flex flex-col gap-1">
        <h1 className="text-h1 text-ink">Sign in</h1>
        <p className="text-body text-ink-secondary">
          {step === "email"
            ? "We'll email you a one-time code. No password to remember."
            : `Enter the code we just emailed you. It expires in ${search.expiresInMinutes ?? 10} minutes.`}
        </p>
      </div>
      {/* ISSUE-1101 R4, the Q227 pattern in 10-sign-in.md: a visitor who
          pressed Start a pool on the marketing site reads why they are here
          in one line above the form. Headline and body stay as they are, and
          there is still no separate sign up step. */}
      {isCreatePoolIntent(search.redirect) ? (
        <p className="text-body-strong text-ink" data-testid="sign-in-context">
          {CREATE_POOL_SIGN_IN_CONTEXT}
        </p>
      ) : null}

      <Card>
        {step === "email" ? (
          // `Card` stacks its children with no gap of its own, so the form and
          // the provider panel share one spaced column here.
          <div className="flex flex-col gap-4">
            <form className="flex flex-col gap-4" onSubmit={onSubmitEmail}>
              <Input
                label="Email address"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                error={emailError}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={isSubmittingEmail || requestOtp.isPending}
                loadingLabel="Sending your code"
              >
                Email me a code
              </Button>
            </form>
            <ProviderSignInPanel
              onSignedIn={({ accessToken, refreshToken }) => {
                signIn({ accessToken, refreshToken });
                void navigate({
                  href: safeRedirectPath(search.redirect),
                  ...REDIRECT_NAVIGATION,
                });
              }}
            />
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={onSubmitCode}>
            <Input
              label="One-time code"
              // A numeric keypad, because the code is digits and this fan is
              // typing with one thumb while switching back from their mail app.
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              error={codeError}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={verifyOtp.isPending}
              loadingLabel="Checking your code"
            >
              Sign in
            </Button>
            {/* The gap between two requests is shown, not enforced silently: a
                fan reads how long is left instead of tapping a control that
                refuses. */}
            <Button
              variant="link"
              fullWidth
              // The label changes every second while the gap runs, so tests and
              // E2E hold onto this instead of a moving name.
              data-testid="sign-in-resend"
              disabled={resend.isDisabled}
              loading={requestOtp.isPending}
              loadingLabel="Sending a new code"
              onClick={onResend}
            >
              {resend.label}
            </Button>
            <Button
              variant="link"
              fullWidth
              onClick={() => {
                setCode("");
                setCodeError(undefined);
                void navigate({
                  to: "/sign-in",
                  search: {
                    step: "email",
                    ...(search.redirect === undefined
                      ? {}
                      : { redirect: search.redirect }),
                  },
                });
              }}
            >
              Use a different email
            </Button>
          </form>
        )}
      </Card>
      {/* Design prompt 10-sign-in.md item 8. Signing in creates the account, so both steps show it. */}
      <SignInFinePrint />
    </div>
  );
};

export const Route = createFileRoute("/sign-in")({
  // Already signed in? There is nothing to do on this screen. Sending them on
  // here rather than in the component keeps the sign-in form from flashing.
  beforeLoad: ({ search }: { search: typeSignInSearch }) => {
    if (getAuthState().status === "signedIn") {
      // See requireSignedIn: `redirect()` returns a Response and throwing it is
      // TanStack Router's own control-flow signal, not an error path.
      // oxlint-disable-next-line typescript/only-throw-error
      throw redirect({
        href: safeRedirectPath(search.redirect),
        ...REDIRECT_NAVIGATION,
      });
    }
  },
  validateSearch: (search: Record<string, unknown>): typeSignInSearch => {
    const rawMinutes = search.expiresInMinutes;
    const parsedMinutes =
      rawMinutes == null || rawMinutes === "" ? undefined : Number(rawMinutes);
    return {
      step: coerceEnum(search.step, SIGN_IN_STEPS, "email"),
      requestId: coerceOptionalString(search.requestId),
      nextRequestAllowedAt: coerceOptionalString(search.nextRequestAllowedAt),
      email: coerceOptionalString(search.email),
      expiresInMinutes:
        parsedMinutes != null &&
        Number.isFinite(parsedMinutes) &&
        parsedMinutes > 0
          ? Math.round(parsedMinutes)
          : undefined,
      redirect: coerceOptionalString(search.redirect),
      deleted: coerceBoolean(search.deleted) ? true : undefined,
    };
  },
  component: SignInScreen,
});
